import { useContext, useEffect, useState } from 'react'
import GlobalContext from '../../../context/global-context'
import businessApi from '../../../api/business.api'
import clsx from 'clsx'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { Text } from '../ui'
import iconVinculos from '../../../assets/iconosCardInformes/informe_9.png'
import isologoSII from '../../../assets/isologo_SII.png'
import linkageTypeLabels from '../views/dashboard/Administracion/Vinculaciones/linkageTypeLabels'
import { formatCurrency, formatDate, formatRut } from '../../utils/formatter'
import { getVinculacionAvailableAmount } from '../../utils/vinculacionAmounts'

import { isPendingConciliation, getCounterpartyType } from '../../utils/pendingConciliation'
const TablaPendientesConciliar = ({ documents = false, movementType, year, selectedRows = [], onChange, excludeId }) => {
  const { currentBusiness } = useContext(GlobalContext)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const params = { businessId: currentBusiness.id, year, month: 0, linkage: true, limit: 10000, max: 10000, page: 0 }
        const response = documents ? await businessApi.getListSummaryMovementsSpecific({ ...params, type: movementType }) : await businessApi.getListSummaryVinculos(params)
        const payload = response?.data
        const items = Array.isArray(payload) ? payload : payload?.libros ?? Object.values(payload ?? {}).flat().filter(item => item && typeof item === 'object')
        if (active) setRows(items.filter(item => String(item.idPrincipal ?? item.id) !== String(excludeId) && isPendingConciliation(item) && (documents || !movementType || getCounterpartyType(item) === movementType)))
      } catch (err) { if (active) setError(true) }
      finally { if (active) setLoading(false) }
    }
    if (currentBusiness?.id && (!documents || movementType)) load()
    else { setRows([]); setLoading(false) }
    return () => { active = false }
  }, [currentBusiness?.id, documents, movementType, year, excludeId])
  if (loading) return <p role='status'>Cargando pendientes...</p>
  if (error) return <p role='alert'>No fue posible cargar los pendientes. Vuelve a abrir la opci?n para reintentar.</p>
  const toggle = row => {
    const id = row.idPrincipal ?? row.id
    const selected = selectedRows.some(item => String(item.idPrincipal ?? item.id) === String(id))
    onChange(selected ? selectedRows.filter(item => String(item.idPrincipal ?? item.id) !== String(id)) : [...selectedRows, { ...row, monto: getVinculacionAvailableAmount(row) }])
  }
  const thClass = 'py-2 px-2 text-center break-words border-b border-gray-200 uppercase text-[0.6rem] font-bold text-gray-500'
  const tdClass = 'py-2 px-2 max-w-[200px] text-center break-words border-b border-gray-100 align-middle'
  const boldClass = 'font-bold text-[0.65rem]'
  const secondaryClass = 'text-[0.705rem] text-gray-400 font-medium'

  return (
    <div className='overflow-x-auto rounded-3xl shadow-sm'>
      <table className='w-full table-auto text-[0.725rem] border-collapse leading-tight'>
        <thead className='sticky top-0 z-10 bg-gray-50'>
          <tr>
            {['', 'Fecha', documents ? 'Documento / Folio' : 'Vinculaci?n / Folio', 'Raz?n Social', 'Detalle', 'Saldo pendiente', 'Estado', 'Acciones'].map((label, index) => (
              <th key={label || 'selection'} className={thClass} scope='col'>
                {index === 0 ? <span className='sr-only'>Seleccionar</span> : label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const id = row.idPrincipal ?? row.id
            const selected = selectedRows.some(item => String(item.idPrincipal ?? item.id) === String(id))
            const folio = row.numeroFolio ?? row.folio ?? row.numeroDocumento
            const status = String(row.status ?? '').trim()
            const partial = status.toUpperCase().includes('PARCIAL')
            return (
              <tr key={id} className='border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                <td className={tdClass}>
                  <div className='flex items-center justify-center gap-2'>
                    <input type='checkbox' className='rounded' aria-label={'Seleccionar ' + (folio ?? id)} checked={selected} onChange={() => toggle(row)} />
                    <img src={documents ? isologoSII : iconVinculos} alt={documents ? 'SII' : 'Vinculaci?n'} className='h-6 w-auto rounded' />
                  </div>
                </td>
                <td className={tdClass}><Text className={boldClass}>{formatDate(row.fecha)}</Text></td>
                <td className={tdClass}>
                  <Text className={boldClass}>{documents ? row.nombreFolio ?? row.tipoDocumento : linkageTypeLabels[row.folioCredito] ?? row.nombreFolio}</Text>
                  <Text className={secondaryClass}>{folio != null ? 'Folio ' + folio : '?'}</Text>
                </td>
                <td className={tdClass}>
                  <Text className={boldClass}>{row.razonSocial}</Text>
                  <Text className={secondaryClass}>{row.rut ? formatRut(row.rut) : ''}</Text>
                </td>
                <td className={tdClass}>
                  <Text className={boldClass}>{row.glosa ?? row.nombreCuenta}</Text>
                  <Text className={secondaryClass}>{row.comprobante}</Text>
                </td>
                <td className={tdClass}><Text className={boldClass}>{formatCurrency(getVinculacionAvailableAmount(row))}</Text></td>
                <td className={tdClass}>
                  <span className={clsx('inline-flex items-center justify-center px-3 py-1 rounded-full text-[0.6rem] font-semibold uppercase tracking-wider', partial ? 'text-amber-700 bg-amber-100' : 'text-danger bg-danger/10')}>
                    {status || '?'}
                  </span>
                </td>
                <td className={tdClass}>
                  <div className='flex flex-col text-xs text-gray-500 items-center justify-center'>
                    <button type='button' aria-label={(selected ? 'Quitar selecci?n ' : 'Seleccionar ') + (folio ?? id)} aria-pressed={selected}
                      className={clsx('flex items-center justify-center w-7 h-7 rounded-full shadow-full-xs transition-colors duration-150', selected ? 'bg-accent2 text-white hover:bg-accent2/90' : 'bg-gray-100 text-gray-300 hover:bg-gray-200')}
                      onClick={() => toggle(row)}>
                      <DoneAllIcon className='w-4 h-4' />
                    </button>
                    <Text className='mt-2'>{selected ? 'Seleccionado' : 'Seleccionar'}</Text>
                  </div>
                </td>
              </tr>
            )
          })}
          {rows.length === 0 && <tr><td colSpan={8} className='py-6 text-center text-gray-500'>No hay pendientes para conciliar</td></tr>}
        </tbody>
      </table>
    </div>
  )
}
export default TablaPendientesConciliar
